import React from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Label } from "@component/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Badge } from "@component/badge";
import {
    Tooltip,
} from "@component/tooltip";
import { UserIcon } from "lucide-react";
import { UserPermissionItem } from "@type/company";
import { updateCompanyPermissions } from "@service/user";
import { accessLevels } from "@constant/app";

import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { useOuraWorkspace } from "@oura/role";

interface EditUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onLoad: () => void;
    user: UserPermissionItem | null;
}

const roleBadgeStyles = {
    write: "bg-blue-100 text-blue-800 hover:bg-blue-100 shadow-none",
    read: "bg-gray-100 text-gray-800 hover:bg-gray-100 shadow-none",
};

export default function EditUserDialog({
    open,
    onOpenChange,
    user,
    onLoad,
}: EditUserDialogProps) {
    const snackbar = useSnackbar();
    // Oura workspaces are internal-only: two roles (Owner + Admin), no
    // read-only Member tier to switch to. Detection comes from the trusted
    // hook in @oura/role (JWT role only in production; URL hint only for
    // non-production previews), same source as AddUserDialog and
    // SettingUserPermissions. Controls stay locked until detection resolves.
    const { isOuraWs, roleReady } = useOuraWorkspace();
    const roleOptions = isOuraWs ? accessLevels.filter((l) => l.value === "write") : accessLevels;
    const [selectedRole, setSelectedRole] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState(false);

    // Once Oura mode is known, normalize a legacy read selection to the only
    // supported tier so the select never holds a value with no visible option.
    React.useEffect(() => {
        if (!roleReady) return;
        if (isOuraWs && selectedRole && selectedRole !== "write") {
            setSelectedRole("write");
        }
    }, [roleReady, isOuraWs, selectedRole]);

    React.useEffect(() => {
        if (user) {
            setSelectedRole(user.permission);
        }
    }, [user]);

    const handleSave = async () => {
        if (!user || !selectedRole) return;

        setIsLoading(true);
        updateCompanyPermissions(user.user_id, selectedRole).then((resp) => {
            if (resp.status === 200) {
                onOpenChange(false);
            } else {
                snackbar.showError(resp.data.message || "Failed to update user role.");
                onOpenChange(false);
            }
        }).catch((error) => {
            catchErrorLog("Failed to update user role:", error);
            snackbar.showError(error.response?.data?.message || "An error occurred while updating the user role.");
            onOpenChange(false);
        }).finally(() => {
            setIsLoading(false);
            onLoad();
        })
    };

    const handleCancel = () => {
        if (user) {
            setSelectedRole(user.permission);
        }
        onOpenChange(false);
    };

    if (!user) return null;

    const hasChanges = selectedRole !== user.permission;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" closeButtonClassName="right-4 top-4">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserIcon className="h-5 w-5 text-gray-600" />
                        Edit Workspace Role
                    </DialogTitle>
                    <DialogDescription>
                        Update this member's workspace role.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* User Info */}
                    <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <UserIcon className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user.first_name} {user.last_name}
                                </p>
                                <Tooltip
                                    delayDuration={500}
                                    side="bottom"
                                    content={<p className="max-w-xs break-all">{user.email}</p>}
                                >
                                    <p className="text-sm text-gray-500 truncate cursor-help">
                                        {user.email}
                                    </p>
                                </Tooltip>
                            </div>
                        </div>
                        <div className="flex justify-start">
                            <Badge
                                variant="secondary"
                                className={
                                    roleBadgeStyles[user.permission as keyof typeof roleBadgeStyles]
                                }
                                style={{
                                    borderRadius: "0.75rem",
                                    paddingTop: "5px",
                                    paddingBottom: "5px",
                                }}
                            >
                                <span className="mr-1">Current:</span>{" "}<span className="capitalize">{user.permission === "write" ? "Admin" : "Member"}</span>
                            </Badge>
                        </div>
                    </div>

                    {/* Role Selection */}
                    <div className="space-y-3">
                        <Label htmlFor="role-select" className="text-sm font-medium">
                            Workspace Role
                        </Label>
                        <Select disabled={isLoading || !roleReady} value={selectedRole} onValueChange={setSelectedRole}>
                            <SelectTrigger id="role-select">
                                <SelectValue placeholder="Select access level">
                                    {selectedRole
                                        ? accessLevels.find((level) => level.value === selectedRole)
                                            ?.label
                                        : "Select access level"}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {roleOptions.map((level) => (
                                    <SelectItem key={level.value} value={level.value}>
                                        <div className="flex flex-col items-start">
                                            <span className="font-medium">{level.label}</span>
                                            <span className="text-sm text-gray-500 max-w-xs">
                                                {level.description}
                                            </span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={!hasChanges || isLoading || !roleReady}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {isLoading ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
